/**
 * Which transcript gyro-svc already holds, per conversation, so the widget neither re-PUTs an unchanged conversation
 * nor forgets to retry one that failed to save.
 *
 * The save effect runs whenever the transcript array changes identity. The inbox poll used to hand it a fresh
 * array every 2.5 s and the whole conversation was PUT again each time (readiness review 2026-09-29); the fix
 * remembers what was saved. Remembering it before the answer arrived was the opposite bug: a 401/403/5xx (a v61
 * custody refusal, say) marked the conversation saved and it was never sent again. A fingerprint therefore counts
 * as saved only after gyro-svc answered 2xx, and an older save that finishes late never overwrites a newer one.
 *
 * Each conversation keeps its own saved and refused fingerprint (PR #242 review follow-up). With one slot for the whole
 * widget, a chat the desktop reopened from its own copy (no load, so nothing marked it saved) was compared with
 * whichever chat was saved last, and its first re-render PUT the unchanged transcript again, which could roll back a
 * newer save from another device; opening one synced chat also threw away the record of another chat's save that was
 * still in flight, and a refusal of one chat was forgotten as soon as another chat was refused.
 */
export class HistorySaveTracker {
    saved = new Map();
    refused = new Map();
    /** conversation id -> fingerprint -> the save of it in flight. */
    inFlight = new Map();
    seq = 0;
    /**
     * Saves begun at or before this number count for nothing when they finish: they belong to an identity that was
     * reset away, or were in flight when a conversation's record was dropped to stay within the bound.
     */
    floor = 0;
    /**
     * True when `fingerprint` is neither the transcript of `conversationId` gyro-svc holds, nor being saved right now,
     * nor the transcript gyro-svc refused for what it is (too large, wrong shape, storage full): re-sending those bytes
     * cannot succeed, and the inbox poll re-renders the same transcript every few seconds, so a refused fingerprint is
     * not retried until the transcript changes.
     */
    needsSave(conversationId, fingerprint) {
        return this.saved.get(conversationId)?.fingerprint !== fingerprint
            && this.refused.get(conversationId) !== fingerprint
            && !this.inFlight.get(conversationId)?.has(fingerprint);
    }
    /** gyro-svc refused `fingerprint` itself (a 400, 413 or 507); the next change to the transcript is tried again. */
    markRefused(conversationId, fingerprint) {
        this.remember(this.refused, conversationId, fingerprint);
    }
    /**
     * Run one save of `fingerprint` for `conversationId`. `run` resolves true only for a 2xx; false, or a throw, leaves
     * the fingerprint unsaved so the next change retries it. Resolves to what `run` reported.
     */
    async save(conversationId, fingerprint, run) {
        const seq = ++this.seq;
        let pending = this.inFlight.get(conversationId);
        if (!pending)
            this.inFlight.set(conversationId, (pending = new Map()));
        pending.set(fingerprint, seq);
        let ok = false;
        try {
            ok = (await run()) === true;
        }
        catch {
            ok = false;
        }
        finally {
            const current = this.inFlight.get(conversationId);
            if (current?.get(fingerprint) === seq) {
                current.delete(fingerprint);
                if (current.size === 0)
                    this.inFlight.delete(conversationId);
            }
        }
        if (ok && seq > this.floor && seq > (this.saved.get(conversationId)?.seq ?? 0)) {
            this.remember(this.saved, conversationId, { fingerprint, seq });
            if (this.refused.get(conversationId) === fingerprint)
                this.refused.delete(conversationId);
        }
        return ok;
    }
    /**
     * Record that gyro-svc already holds `fingerprint` for `conversationId` because the transcript was just loaded FROM
     * it. Opening a synced conversation must not PUT it straight back: that re-uploaded the snapshot this device loaded,
     * bumped updatedAt on every view, and could roll back a newer save from another device.
     */
    markSaved(conversationId, fingerprint) {
        this.remember(this.saved, conversationId, { fingerprint, seq: ++this.seq });
    }
    /** Forget everything (a different signed-in user). */
    reset() {
        this.saved.clear();
        this.refused.clear();
        this.inFlight.clear();
        this.floor = this.seq;
    }
    /**
     * Set `conversationId`'s record, most recent last, keeping at most HISTORY_SAVE_TRACKED_CONVERSATIONS. Dropping the
     * oldest only ever costs one more PUT of that chat on its next change; the saves in flight at that moment are not
     * recorded when they finish, so a dropped record can never come back as a "saved" mark for a transcript that a later
     * save already replaced.
     */
    remember(records, conversationId, value) {
        records.delete(conversationId);
        records.set(conversationId, value);
        if (records.size <= HISTORY_SAVE_TRACKED_CONVERSATIONS)
            return;
        const oldest = records.keys().next().value;
        if (oldest !== undefined)
            records.delete(oldest);
        this.floor = this.seq;
    }
}
/** Conversations whose save state is remembered: gyro-svc keeps at most 100 per person on one app (history.ts MAX_PER_USER). */
export const HISTORY_SAVE_TRACKED_CONVERSATIONS = 100;
/** Whether starting a new chat lets the person keep saving after this refusal (full storage is not per chat). */
export function newChatHelps(view) {
    return view.state === 'refused' && view.reason !== 'history_store_full' && view.reason !== 'history_person_full';
}
/**
 * The sentence gyro-svc wrote for a retryable refusal that names its limit: the conversation-save rate limit (the
 * limit and when to retry). Any other body (a proxy's error page, a framework's internal error text) is never shown.
 */
function serverRetryText(outcome) {
    const text = outcome.message?.trim();
    return outcome.status === 429 && text && text.length <= 400 && !/[<>]/.test(text) ? text : undefined;
}
/**
 * A save gyro-svc refused for want of identity (a 401, or a 403 that is not the host's "not proxied"). On the desktop the
 * agent signs the page's calls, and for a few seconds around its own sign-in it cannot (owner, 2026-10-27 11:33 PT: "a
 * sign-in is in progress" in the agent's log, and the notice below on his screen; "it goes away but it shouldnt show up"):
 * such a save is tried again quietly, after these delays, before the person is told.
 */
export function identityRefusal(outcome) {
    return outcome.retryable && (outcome.status === 401 || outcome.status === 403 && outcome.error !== 'gyro_path_not_proxied');
}
export const historyIdentityRetry = { delaysMs: [3_000, 10_000, 30_000] };
/** The sentence for a save that did not go through, from the server's reason when it gave one. */
export function historySaveMessage(outcome, desktop = false) {
    if (outcome.retryable) {
        // The host's proxy does not pass history saves through to gyro-svc (a 405, or its own 403 naming the route): no
        // sign-in fixes that, so say what is true instead.
        if (outcome.status === 405 || (outcome.status === 403 && outcome.error === 'gyro_path_not_proxied')) {
            return 'This app does not keep Gyro chats in your history yet. This chat stays on this device.';
        }
        if (outcome.status === 401 || outcome.status === 403) {
            // The desktop app signs the person in itself; nobody is asked to.
            return desktop
                ? 'The latest change to this chat was not saved to your history: Gyro could not confirm who you are. It stays on this device, and Gyro syncs it once it has confirmed who you are.'
                : 'The latest change to this chat was not saved to your history: Gyro could not confirm who you are. It stays on this device; sign in again to keep syncing.';
        }
        const reason = serverRetryText(outcome);
        return reason
            ? `The latest change to this chat has not been saved to your history yet: ${reason} It stays on this device; Gyro retries when the chat changes.`
            : 'The latest change to this chat has not been saved to your history yet. It stays on this device; Gyro retries when the chat changes.';
    }
    if (outcome.error === 'conversation_too_large') {
        const limit = outcome.limit !== undefined ? ` (${outcome.limit.toLocaleString('en-US')})` : '';
        return `This chat has grown past the size Gyro can save to your history${limit}. It stays on this device, but new messages here are not synced or searchable. Start a new chat to keep saving.`;
    }
    // The server's sentences for the two storage limits are written for admins (they name settings); say what the
    // person can do instead.
    if (outcome.error === 'history_person_full') {
        return 'This chat was not saved to your history: your saved chats have reached their storage limit. Delete older chats you no longer need to make room. Nothing already saved was lost, and this chat stays on this device.';
    }
    if (outcome.error === 'history_store_full') {
        return "This chat was not saved to your history because Gyro's shared chat storage is full. Nothing already saved was lost, and this chat stays on this device. Let your IT team know so they can make room.";
    }
    return `This chat was not saved to your history${outcome.message ? `: ${outcome.message}` : '.'} It stays on this device.`;
}
//# sourceMappingURL=historySave.js.map